import Pagination from '@mui/material/Pagination';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TagIcon from '@mui/icons-material/Tag';
import GroupIcon from '@mui/icons-material/Group';
import NoDataImg from '../../../assets/img/no-data.gif';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import urls from '../../constants/apiurl';
import ApiCaller from '../../constants/ApiCaller';
import { showDangerToast } from '../../constants/toaster';
import Utils from '../../constants/Utils';
import { buildTabCacheKey, getTabCache, setTabCache } from '../../lib/tabFetchCache';

const readListCache = (userId, referralCode, inviteType, page) => {
  if (!userId || !referralCode) return null;
  return getTabCache(
    buildTabCacheKey(userId, 'invited-users', referralCode, inviteType || 'default', page)
  );
};

export const InvitedUserList = ({
  userId,
  referralCode,
  inviteType = null,
  enabled = true,
  referralCodeLoading = false,
  invitedByName = '',
  invitedByUserId = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { formatDateTime } = Utils();
  const userData = useSelector((state) => state?.userData);
  const initialCache = readListCache(userId, referralCode, inviteType, 1);
  const [datalist, setDatalist] = useState(initialCache?.items || []);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(initialCache?.pagerecord?.pageNumber || 1);
  const [pagerecord, setPagerecord] = useState(
    initialCache?.pagerecord || {
      totalItems: 0,
      pageNumber: 1,
      pageSize: 10,
      totalPages: 1,
    }
  );
  const lastFetchKeyRef = useRef(null);

  useEffect(() => {
    if (!enabled || !referralCode) return;

    const cacheKey = buildTabCacheKey(
      userId,
      'invited-users',
      referralCode,
      inviteType || 'default',
      page
    );

    if (lastFetchKeyRef.current === cacheKey) return;

    const cached = getTabCache(cacheKey);
    if (cached) {
      lastFetchKeyRef.current = cacheKey;
      setDatalist(cached.items || []);
      setPagerecord(cached.pagerecord || pagerecord);
      setLoading(false);
      return;
    }

    getUserList(page, cacheKey);
  }, [page, referralCode, inviteType, enabled, userId]);

  useEffect(() => {
    if (!enabled) return;
    setPage(1);
    lastFetchKeyRef.current = null;
  }, [referralCode, inviteType, enabled]);

  const getUserList = async (pageNo, cacheKey) => {
    try {
      setLoading(true);
      const postJson = {
        pageNumber: pageNo - 1 || 0,
        pageSize: pagerecord.pageSize || 10,
        shortingField: 'createdAt',
        asc: true,
        referralCode,
      };
      if (inviteType) {
        postJson.inviteType = inviteType;
      }

      const res = await ApiCaller.post(postJson, urls.invitedUserList, userData?.access_token);
      const response = await res.response.json();

      if (response.status == 200) {
        const listData = response?.data?.response || response?.data || {};
        const items = listData?.items || [];
        const nextPagerecord = {
          totalItems: listData?.totalItems || 0,
          totalPages: listData?.totalPages || 1,
          pageNumber: pageNo,
          pageSize: 10,
        };
        setDatalist(items);
        setPagerecord(nextPagerecord);
        lastFetchKeyRef.current = cacheKey;
        if (cacheKey) {
          setTabCache(cacheKey, { items, pagerecord: nextPagerecord });
        }
      } else {
        showDangerToast(response.message);
      }
    } catch (error) {
      console.error(error);
      showDangerToast(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    lastFetchKeyRef.current = null;
    setPage(value);
  };

  const openUser = (userIdToOpen) => {
    if (userIdToOpen) navigate(`/user-manager/${userIdToOpen}`);
  };

  const referrerName = invitedByName?.trim();
  const totalInvited = pagerecord?.totalItems ?? 0;

  const headerChips = (
    <Stack
      direction="row"
      flexWrap="wrap"
      useFlexGap
      spacing={1}
      sx={{ mb: 2, alignItems: 'center' }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: { sm: 1 }, width: { xs: '100%', sm: 'auto' } }}>
        Invited users
      </Typography>
      {referrerName ? (
        <Chip
          size="small"
          icon={<PersonAddIcon />}
          label={`Referred by ${referrerName}`}
          color="primary"
          variant="outlined"
          onClick={invitedByUserId ? () => openUser(invitedByUserId) : undefined}
          sx={invitedByUserId ? { cursor: 'pointer' } : undefined}
        />
      ) : (
        <Chip size="small" icon={<PersonAddIcon />} label="No referrer on file" variant="outlined" />
      )}
      {referralCode && (
        <Chip size="small" icon={<TagIcon />} label={referralCode} variant="outlined" />
      )}
      {totalInvited > 0 && (
        <Chip
          size="small"
          icon={<GroupIcon />}
          label={`${totalInvited} invited`}
          variant="filled"
          color="default"
        />
      )}
    </Stack>
  );

  if (referralCodeLoading) {
    return (
      <>
        {headerChips}
        <Box className="text-center" py={3}>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </>
    );
  }

  if (!referralCode) {
    return (
      <>
        {headerChips}
        <Box className="text-center" py={3}>
          <Typography variant="body2" color="text.secondary">
            {inviteType === 'CREATERINVITE'
              ? 'No creator referral code available for this user.'
              : 'No referral code available for this user.'}
          </Typography>
        </Box>
      </>
    );
  }

  if (loading && !datalist?.length) {
    return (
      <>
        {headerChips}
        <Box className="text-center" py={3}>
          <Typography variant="body2" color="text.secondary">
            Loading invited users...
          </Typography>
        </Box>
      </>
    );
  }

  if (!datalist?.length) {
    return (
      <>
        {headerChips}
        <Box className="text-center" py={3}>
          <img width="22%" src={NoDataImg} alt="No data" style={{ maxWidth: 120 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
            No invited users yet
          </Typography>
        </Box>
      </>
    );
  }

  const startIndex = (pagerecord.pageNumber - 1) * pagerecord.pageSize;

  if (isMobile) {
    return (
      <>
        {headerChips}
        <Stack spacing={1.5}>
          {datalist.map((data, i) => (
            <Paper
              key={data?.id || i}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 1.5 }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: '#1976d2',
                  fontWeight: 600,
                  cursor: 'pointer',
                  mb: 0.5,
                }}
                onClick={() => openUser(data?.id)}
              >
                {data?.userName || '—'}
              </Typography>
              <Stack spacing={0.25}>
                <Typography variant="caption" color="text.secondary">
                  Mobile: {data?.contactNumber || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Videos: {data?.videos ?? '—'} · Joined{' '}
                  {data?.createdAt ? formatDateTime(data.createdAt) : '—'}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
        {pagerecord.totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Pagination
              className="custom-pagination-class"
              count={pagerecord.totalPages}
              page={page}
              onChange={handlePageChange}
              color="secondary"
              size="small"
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      {headerChips}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: 48 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 72 }}>Videos</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Registered</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datalist.map((data, i) => (
              <TableRow key={data?.id || i} hover>
                <TableCell>{startIndex + i + 1}</TableCell>
                <TableCell
                  sx={{
                    color: '#1976d2',
                    cursor: 'pointer',
                    fontWeight: 500,
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => openUser(data?.id)}
                >
                  {data?.userName || '—'}
                </TableCell>
                <TableCell>{data?.contactNumber || '—'}</TableCell>
                <TableCell>{data?.videos ?? '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                  {data?.createdAt ? formatDateTime(data.createdAt) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {pagerecord.totalPages > 1 && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Pagination
            className="custom-pagination-class"
            count={pagerecord.totalPages}
            page={page}
            onChange={handlePageChange}
            color="secondary"
            size="small"
          />
        </Box>
      )}
    </>
  );
};
